import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { api, WHATSAPP_LINK } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { MessageCircle } from "lucide-react";

const SECTION_CONFIG = {
  "3d": {
    title: "Figuras 3D",
    italic: "coleccionables",
    desc: "Pokémon, dragones, juguetes articulados, anime y merchandising. Hechos con cariño en Copiapó.",
  },
  papeleria: {
    title: "Papelería Kawaii",
    italic: "personalizada",
    desc: "Libros para colorear con tu nombre, agendas kawaii, stickers y vinilos adhesivos.",
  },
};

const Catalog = () => {
  const location = useLocation();
  const section = location.pathname.includes("papeleria") ? "papeleria" : "3d";
  const config = SECTION_CONFIG[section] || SECTION_CONFIG["3d"];

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");

  useEffect(() => {
    setLoading(true);
    setActive("all");
    Promise.all([
      api.get(`/products?section=${section}`),
      api.get(`/categories?section=${section}`),
    ]).then(([p, c]) => {
      setProducts(p.data);
      setCategories(c.data);
      setLoading(false);
    });
  }, [section]);

  const filtered = useMemo(() => {
    if (active === "all") return products;
    return products.filter((p) => p.category === active);
  }, [active, products]);

  const filterOptions = [{ slug: "all", name: "Todos" }, ...categories];

  return (
    <div data-testid={`catalog-${section}`} className="pt-12 pb-24 min-h-screen bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">
            ─ Catálogo ─
          </span>
          <h1 className="mt-3 font-heading text-5xl md:text-6xl font-extrabold tracking-tight text-brand-ink leading-tight">
            {config.title}<br />
            <span className="font-accent italic font-semibold text-mint-deep">{config.italic}</span>.
          </h1>
          <p className="mt-5 text-gray-700 max-w-2xl leading-relaxed">{config.desc}</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          {filterOptions.map((c) => (
            <button
              key={c.slug}
              onClick={() => setActive(c.slug)}
              data-testid={`filter-${c.slug}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border-2 border-black rounded-full transition-all hover:-translate-y-0.5 ${
                active === c.slug
                  ? "bg-brand-ink text-white shadow-[3px_3px_0_0_#EC4899]"
                  : "bg-white text-brand-ink hover:bg-yolk hover:text-white hover:shadow-[3px_3px_0_0_#1A1333]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-mint-light/50 animate-pulse border-2 border-black" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border-2 border-black">
            <p className="text-gray-500">Aún no hay productos en esta categoría.</p>
            <a href={WHATSAPP_LINK("Hola, quiero cotizar un proyecto personalizado")} target="_blank" rel="noreferrer" className="btn-chunky inline-flex items-center gap-2 bg-yolk text-black px-6 py-3 mt-6 text-sm">
              <MessageCircle className="h-4 w-4" /> Cotiza tu proyecto
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="catalog-grid">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        <div className="mt-16 text-center bg-white border-2 border-black p-8">
          <h3 className="font-heading text-2xl md:text-3xl font-bold mb-3">¿No encuentras lo que buscas?</h3>
          <p className="text-gray-600 mb-6">Hacemos figuras y papelería 100% a tu medida. Cuéntanos tu idea por WhatsApp.</p>
          <a href={WHATSAPP_LINK("Hola, quiero cotizar mi proyecto personalizado")} target="_blank" rel="noreferrer" data-testid="catalog-cta-wa" className="btn-chunky inline-flex items-center gap-3 bg-yolk text-black px-8 py-4">
            <MessageCircle className="h-5 w-5" /> COTIZA TU PROYECTO PERSONALIZADO
          </a>
        </div>
      </div>
    </div>
  );
};

export default Catalog;
